'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { useAskComposerAdapter } from '#pplx/frontend/apps/ask/ask-composer/askComposerAdapter.js';
import { createHomeComposerFocusHandle } from '#pplx/frontend/apps/ask/ask-composer/createHomeComposerFocusHandle.js';
import { FloatWhileOpen } from '#pplx/frontend/apps/ask/ask-composer/FloatWhileOpen.js';
import { HomeMobileOverlayShell, useHomeComposerMobileOverlay, } from '#pplx/frontend/apps/ask/ask-composer/homeMobileOverlay.js';
import { getAskComposerSurfaceConfig } from '#pplx/frontend/apps/ask/ask-composer/internal/surfaceConfig.js';
import { useAskComposerSdkPlugins } from '#pplx/frontend/apps/ask/ask-composer/useAskComposerSdkPlugins.js';
import { ConnectorFilePickerProvider } from '#pplx/frontend/apps/ask/connectors-file-pickers/ConnectorFilePickerProvider.js';
import { useInputActions } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { useCallbackRef } from '#pplx/frontend/common/hooks/useCallbackRef.js';
import { invariant } from '#pplx/frontend/common/invariant.js';
import { Composer } from '#pplx/frontend/composer-sdk/Composer.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { useCallback, useImperativeHandle } from 'react';
import { defineMessage, useIntl } from 'react-intl';
const homePlaceholder = defineMessage({
    defaultMessage: 'Ask anything…',
    description: 'Default placeholder text for the main ask input field',
    id: 'e8321de9f8',
});
export function AskComposer({ surface, inputFocusRef, onAcceptedSubmit, pageIntegration: pageProps, recessedFrame = false, }) {
    const intl = useIntl();
    const placeholder = intl.formatMessage(homePlaceholder);
    const { isMobileStyle, isMobileUserAgent } = useIsMobile();
    const { setUserInput } = useInputActions();
    const surfaceConfig = getAskComposerSurfaceConfig(surface);
    const onActivateBlankSuggestions = useCallbackRef(() => pageIntegration.onClick());
    const resetAutosuggestions = useCallbackRef(() => autosuggest.resetAutosuggestions());
    const onClear = useCallback(() => setUserInput(''), [setUserInput]);
    const onCommandMenuOpenChange = useCallbackRef((open) => typeahead?.commandMenu?.onOpenChange?.(open));
    const { composerRef, composerProps: mobileOverlayProps, shellProps, } = useHomeComposerMobileOverlay({
        isMobileWeb: surfaceConfig.mobileOverlayEnabled && isMobileStyle && isMobileUserAgent,
        onActivateBlankSuggestions,
        resetAutosuggestions,
        onClear,
        onCommandMenuOpenChange,
    });
    const onFocus = useCallback(() => {
        const composer = composerRef.current;
        // A queued connector focus can run after the frame unmounts.
        if (composer === null)
            return;
        composer.focus();
    }, [composerRef]);
    const onInsertMention = useCallback(({ uuid, text, variant, localizedName, url, icon, prefixWithSlash }) => {
        invariant(composerRef.current !== null, 'Ask Composer handle is required');
        if (variant === 'plugin') {
            composerRef.current.insertMention({
                id: uuid,
                kind: 'plugin',
                text,
                localizedName,
                url,
                icon: icon?.type === 'source' ? undefined : icon,
                prefixWithSlash,
            });
            return;
        }
        composerRef.current.insertMention({
            id: uuid,
            kind: 'source',
            text,
            icon: { type: 'source', sourceId: uuid },
        });
    }, [composerRef]);
    const onRemoveMention = useCallback(({ uuid }) => {
        invariant(composerRef.current !== null, 'Ask Composer handle is required');
        composerRef.current.removeMention({ id: uuid, kind: 'source' });
    }, [composerRef]);
    const resetAfterAcceptedSubmit = useCallbackRef(() => {
        invariant(composerRef.current !== null, 'Ask Composer handle is required');
        autosuggest.resetAutosuggestions();
        composerRef.current.reset();
    });
    const handleAcceptedSubmit = useCallback(() => {
        onAcceptedSubmit?.();
        resetAfterAcceptedSubmit();
    }, [onAcceptedSubmit, resetAfterAcceptedSubmit]);
    const onBeforeSubmit = useCallbackRef(() => stopTranscriptionIfActive());
    const { attachments, autosuggestControls, modeControls, onDictationSubmit, characterCount, isVisible, missingAttachmentGuard, project, onProjectChange, remoteBoxPlugin, ...controller } = useAskComposerAdapter({
        surfaceConfig,
        onAcceptedSubmit: handleAcceptedSubmit,
        onBeforeSubmit,
    });
    const { autosuggest, pageIntegration, plugins, typeahead, isComputerCommandMenuOpen, placeholder: dictationPlaceholder, stopTranscriptionIfActive, } = useAskComposerSdkPlugins({
        surfaceConfig,
        ...modeControls,
        onFocus,
        onInsertMention,
        onRemoveMention,
        autosuggestControls,
        composerRef,
        pageIntegration: pageProps,
        composerValue: controller.value,
        isSubmitDisabled: controller.isSubmitDisabled,
        onDictationSubmit,
        attachments,
        characterCount,
        isVisible,
        project,
        onProjectChange,
        remoteBoxPlugin,
    });
    useImperativeHandle(inputFocusRef, () => createHomeComposerFocusHandle(composerRef), [
        composerRef,
    ]);
    const displayedPlaceholder = dictationPlaceholder ?? pageIntegration.composerProps.placeholder;
    const ariaLabel = pageIntegration.composerProps.ariaLabel ?? displayedPlaceholder ?? placeholder;
    const composer = (_jsx(BoxPrimitive, { onKeyDown: pageIntegration.onKeyDown, children: _jsx(Composer, { value: controller.value, externalValueCaret: surfaceConfig.externalValueCaret, onSubmit: controller.onSubmit, isSubmitDisabled: controller.isSubmitDisabled, status: controller.status, ...pageIntegration.composerProps, onClick: pageIntegration.onClick, ...mobileOverlayProps, onValueChange: autosuggest.onComposerValueChange, ref: composerRef, ariaLabel: ariaLabel, placeholder: displayedPlaceholder, enterKeyBehavior: isMobileUserAgent ? 'newline' : 'submit', minRows: 2, maxRows: 10, plugins: plugins, typeahead: typeahead }) }));
    const frame = surfaceConfig.floatWhileCommandMenuOpen ? (_jsx(FloatWhileOpen, { open: isComputerCommandMenuOpen, children: composer })) : (composer);
    const content = (_jsxs(ConnectorFilePickerProvider, { children: [frame, attachments.previewOverlay, missingAttachmentGuard] }));
    return shellProps ? (_jsx(HomeMobileOverlayShell, { ...shellProps, recessedFrame: recessedFrame, children: content })) : (content);
}
//# sourceMappingURL=AskComposer.js.map